import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import useSeo from "@/hooks/useSeo";
import { mangaList, type MangaItem } from "@/mocks/manga";

type RankKey = "popular" | "rating" | "new" | "finished";

const tabs: { key: RankKey; label: string }[] = [
  { key: "popular", label: "人气榜" },
  { key: "rating", label: "评分榜" },
  { key: "new", label: "新作榜" },
  { key: "finished", label: "完结榜" },
];

function sortManga(key: RankKey): MangaItem[] {
  if (key === "rating") return [...mangaList].sort((a, b) => b.rating - a.rating);
  if (key === "new") return mangaList.filter((m) => m.status === "连载中").slice(0, 10);
  if (key === "finished") return mangaList.filter((m) => m.status === "已完结").concat(
    [...mangaList].sort((a, b) => a.popular - b.popular)
  );
  return [...mangaList].sort((a, b) => a.popular - b.popular);
}

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "笨狗漫画网排行榜",
    url: "https://comicsur.github.io/rank",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://comicsur.github.io/" },
      { "@type": "ListItem", position: 2, name: "排行榜", item: "https://comicsur.github.io/rank" },
    ],
  },
];

export default function RankPage() {
  const [active, setActive] = useState<RankKey>("popular");
  const list = sortManga(active);

  useSeo({
    title: "漫画排行榜 - 人气榜、评分榜、新作榜 | 笨狗漫画网",
    description:
      "笨狗漫画网漫画排行榜，涵盖人气榜、评分榜、新作榜与完结榜，为你整理当前最值得追的热门漫画作品。",
    jsonLd,
  });

  return (
    <PageLayout>
      <div className="bg-dots">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
          <nav className="flex items-center gap-2 text-sm text-foreground-600" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
            <span className="text-foreground-950">排行榜</span>
          </nav>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="font-heading text-3xl text-foreground-950 md:text-4xl">漫画排行榜</h1>
              <p className="mt-2 text-sm text-foreground-600">每日更新，看榜下单，追更不迷路</p>
            </div>
          </div>

          <div className="mt-6 flex w-max items-center rounded-full bg-background-100 p-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActive(tab.key)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  active === tab.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:bg-background-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {list.map((manga, idx) => (
              <article
                key={manga.id}
                className="group flex items-center gap-4 rounded-2xl bg-background-100 p-4 transition-colors hover:bg-background-200"
                data-product-shop
              >
                <span
                  className={`flex h-12 w-10 shrink-0 items-center justify-center rounded-lg font-heading text-xl ${
                    idx < 3 ? "bg-primary-500 text-background-50" : "bg-secondary-200 text-secondary-800"
                  }`}
                >
                  {idx + 1}
                </span>
                <img
                  src={manga.cover}
                  alt={`${manga.title}漫画封面`}
                  className="h-24 w-16 shrink-0 rounded-lg object-cover object-top"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="truncate text-sm font-bold text-foreground-950 group-hover:text-primary-600 md:text-base">
                      {manga.title}
                    </h2>
                    <span className="shrink-0 rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                      {manga.status}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-foreground-600">
                    {manga.author} · {manga.genre} · {manga.chapters}
                  </p>
                  <p className="mt-1 line-clamp-1 text-xs text-foreground-600">{manga.intro}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {manga.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span className="flex items-center gap-1 text-base font-bold text-accent-600">
                    <i className="ri-star-fill text-sm text-accent-500" aria-hidden="true"></i>
                    {manga.rating}
                  </span>
                  <span className="text-xs text-foreground-600">{manga.ratingCount} 人评分</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}